import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Clock, Receipt, AlertCircle } from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { orderLabel } from '../utils/itemName';

/**
 * The customer's copy of a bill, opened from a WhatsApp message or a scanned QR
 * code.
 *
 * Rendered outside the app shell and outside ProtectedRoute: whoever opens this
 * has no login, and bouncing them to a sign-in screen would make the whole
 * feature pointless. The share token in the URL is the only credential.
 *
 * Deliberately not using the shared axios client - that one attaches the
 * cashier's bearer token and tenant header from localStorage, which is wrong
 * here and would leak a staff credential onto a public page if it ever ran in a
 * browser that had one.
 */
function PublicBill() {
  const { t, language } = useLanguage();
  const { shareToken } = useParams();
  const [bill, setBill] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    fetch(`${BACKEND_URL}/api/public/bill/${shareToken}`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.message || 'This bill could not be found.');
        return body;
      })
      .then((data) => {
        if (active) setBill(data);
      })
      .catch((fetchError) => {
        if (active) setError(fetchError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [shareToken]);

  const money = (value) => `Rs ${(value ?? 0).toFixed(2)}`;

  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString([], {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '';

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-cafe-50 flex items-center justify-center p-4">
        <p className="text-sm text-gray-500">{t('Loading bill...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] bg-cafe-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center max-w-sm w-full">
          <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h1 className="text-base font-semibold text-gray-900 mb-1">{t('Bill unavailable')}</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-cafe-50 py-6 px-4 print:bg-white print:p-0">
      <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden print:shadow-none print:border-0">
        <div className="text-center px-5 pt-6 pb-4 border-b border-dashed border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">{bill.tenantName}</h1>
          <p className="text-xs text-gray-500 mt-1">Token #{bill.tokenNumber}</p>
          <p className="text-xs text-gray-500">{formatDateTime(bill.issuedAt)}</p>
          {bill.billedTo ? (
            <p className="text-sm text-gray-700 mt-2">Billed to {bill.billedTo}</p>
          ) : null}
        </div>

        <div className="px-5 py-4 space-y-2">
          {bill.items.map((line, index) => (
            <div key={index} className="flex justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="text-gray-900">{orderLabel(line, language)}</span>
                {line.quantity > 1 && (
                  <span className="text-gray-500">
                    {' '}
                    × {line.quantity} @ {money(line.unitPrice)}
                  </span>
                )}
              </span>
              <span className="flex-shrink-0 tabular-nums text-gray-900">{money(line.amount)}</span>
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-dashed border-gray-200 space-y-1.5">
          {bill.discount > 0 && (
            <>
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('Subtotal')}</span>
                <span className="tabular-nums">{money(bill.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-700">
                <span>{t('Discount')}</span>
                <span className="tabular-nums">-{money(bill.discount)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between items-baseline pt-1">
            <span className="font-semibold text-gray-900">{t('Total')}</span>
            <span className="text-2xl font-bold text-cafe-700 tabular-nums">
              {money(bill.total)}
            </span>
          </div>
        </div>

        <div className="px-5 pb-6">
          {bill.status === 'paid' && !bill.onAccount ? (
            <p className="flex items-center justify-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-xl py-2.5">
              <CheckCircle2 className="h-4 w-4" />
              Paid{bill.paymentType ? ` by ${bill.paymentType}` : ''}
            </p>
          ) : bill.onAccount ? (
            <p className="flex items-center justify-center gap-2 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl py-2.5">
              <Clock className="h-4 w-4" />
              {t('On account - payment due')}
            </p>
          ) : (
            <p className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 bg-gray-50 border border-gray-200 rounded-xl py-2.5">
              <Receipt className="h-4 w-4" />
              {t('Unpaid')}
            </p>
          )}
        </div>
      </div>

      <p className="text-center text-xs text-gray-400 mt-4 print:hidden">
        {t('Thank you for your visit')}
      </p>
    </div>
  );
}

export default PublicBill;
